import { FC, useMemo } from 'react';
import useImage from 'use-image';
import { Image, Layer, Stage } from 'react-konva';
import { DefaultCartModelDisplay } from '../CartModelDisplay';
import CartModelDisplayLoading from '../CartModelDisplayLoading/CartModelDisplayLoading';
import useCartModelDisplay from '../../../hooks/useCartModelDisplay';
import { Wheels14InLiftedX4, Wheels14InX4 } from '../../../data/products/Carts/XSeriesProducts/Shared/Wheels';

interface X4ModelProps {
  defaults?: DefaultCartModelDisplay,
  width?: number,
  height?: number
}

const X4Model: FC<X4ModelProps> = ({
  defaults,
  width = 1000,
  height = 1000
}) => {
  const { chassisType, bodyColor, liftKit, wheel, wheelSize, frontSeat } = useCartModelDisplay();

  const defaultChassisImage = chassisType?.liftKitOptions[1].configuratorImg ?? '';
  const chassisImageUrl = useMemo<String>(() => {
    let chassisImageUrl: String = '';
    if (defaults) {
      chassisImageUrl = defaults.liftKit?.configuratorImg ?? '';
    } else {
      chassisImageUrl = liftKit?.configuratorImg ?? defaultChassisImage;
    }
    return chassisImageUrl;
  }, [defaults, liftKit, defaultChassisImage]);

  const liftKitSku = liftKit?.sku;
  const wheelImageUrl = useMemo<String>(() => {
    let wheelImageUrl: String = '';
    if (defaults) {
      wheelImageUrl = defaults.wheel?.configuratorImg ?? '';
    } else {
      if (wheel?.configuratorImg) {
        wheelImageUrl = wheel?.configuratorImg;
      } else {
        wheelImageUrl =
          liftKitSku === 'L'
            ? Wheels14InLiftedX4[0].configuratorImg ?? ''
            : Wheels14InX4[0].configuratorImg ?? ''
      }
    }
    return wheelImageUrl;
  }, [defaults, wheel, liftKitSku]);

  const bodyColorRef = bodyColor?.refId;
  const seatsImageUrl = useMemo<String>(() => {
    let seatsImageUrl: String = '';
    const matchingSeatOption = chassisType?.frontSeatOptions.find((seat) => seat.refId === bodyColor?.refId);
    if (defaults) {
      seatsImageUrl = defaults.frontSeat?.configuratorImg ?? '';
    } else {
      if (frontSeat?.configuratorImg) {
        seatsImageUrl = frontSeat?.configuratorImg;
      } else {
        seatsImageUrl =
          matchingSeatOption?.configuratorImg ??
          chassisType?.frontSeatOptions[0].configuratorImg ?? '';
      }
    }
    return seatsImageUrl;
  }, [defaults, chassisType, frontSeat, bodyColorRef]);

  const defaultBodyColorImage = chassisType?.bodyColorOptions[0].configuratorImg ?? '';
  const bodyColorImageUrl = useMemo<String>(() => {
    let bodyColorImageUrl: String = '';
    if (defaults) {
      bodyColorImageUrl = defaults.bodyColor?.configuratorImg ?? '';
    } else {
      bodyColorImageUrl = bodyColor?.configuratorImg ?? defaultBodyColorImage;
    }
    return bodyColorImageUrl;
  }, [defaults, bodyColor, defaultBodyColorImage]);

  const tireImageUrl = useMemo<String>(() => {
    let tireImageUrl: String = '';
    if (wheelSize === -1 || wheelSize === undefined) {
      tireImageUrl =
        liftKitSku === 'L' || defaults?.liftKit?.sku === 'L'
          ? 'https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/df1b12f9-66ef-4679-5f96-f90830cf7f00'
          : 'https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/b174ee79-b187-4f12-44d8-82e52ce1d800'
    } else if (wheelSize === 12) {
      tireImageUrl =
        liftKitSku === 'L' || defaults?.liftKit?.sku === 'L'
          ? 'https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/fdc4a063-5980-41ae-6fb4-c69fbd649c00'
          : 'https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/629e994e-01c1-430d-1b91-5032b7c26a00'

    } else if (wheelSize === 14) {
      tireImageUrl =
        liftKitSku === 'L' || defaults?.liftKit?.sku === 'L'
          ? 'https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/df1b12f9-66ef-4679-5f96-f90830cf7f00'
          : 'https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/b174ee79-b187-4f12-44d8-82e52ce1d800'

    } else if (wheelSize === 15) {
      tireImageUrl =
        liftKitSku === 'L' || defaults?.liftKit?.sku === 'L'
          ? 'https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/fd58a083-5cfb-4452-6e78-459db75a3f00'
          : 'https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/01d4a429-ea3d-4f80-412b-7031cf966d00'
    }
    return tireImageUrl;
  }, [defaults, liftKitSku, wheelSize]);

  const rockerPanelImageUrl = "https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/c023dbe6-4991-42a0-78ea-0f8c8589ee00";

  const fenderFlaresImageUrl = useMemo<String>(() => {
    let fenderFlaresImageUrl: String =
      liftKitSku === 'L' || defaults?.liftKit?.sku === 'L'
        ? 'https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/c9578747-716a-4e7b-fb5e-05b7e20aa300'
        : 'https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/f41bdf4c-32b5-43a8-6f7f-aca342fb5700' // Empty image
    return fenderFlaresImageUrl;
  }, [defaults, liftKitSku]);

  const brushGuardImageUrl = useMemo<String>(() => {
    let brushGuardImageUrl: String =
      liftKitSku === 'L' || defaults?.liftKit?.sku === 'L'
        ? 'https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/687b1dfe-de45-400e-8ca4-860fcbe6a000'
        : 'https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/f41bdf4c-32b5-43a8-6f7f-aca342fb5700' // Empty image
    return brushGuardImageUrl;
  }, [defaults, liftKitSku]);

  const rearStrutsImageUrl = "https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/d8def297-e7f7-469c-0535-de36e0e20f00";
  const sideMirrorsImageUrl = "https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/c2e8d27f-364a-4e55-19f2-d36329526300";
  const steeringWheelImageUrl = "https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/09c5210f-ddf6-4074-d9a3-4a9f08dde400";
  const windshieldImageUrl = "https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/0f83ad74-01c4-4595-8bc5-9807ea229300";
  const topImageUrl = "https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/5e271630-ffa2-4937-7ebc-79effd379f00";
  const decalsImageUrl = "https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/0f545444-91e2-40fd-0012-9df0df1b6200";
  const rearViewMirrorImageUrl = "https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/6d1881b7-b824-4b33-d979-764bd1186b00";
  const rearCellPhoneHolderImageUrl = "https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/0ef354ed-faa5-459f-bf60-2d7e986b5200";
  const dashImageUrl = "https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/a8765800-3eb4-4296-c35a-89fdbc8a1a00";

  const frontBumperP1ImageUrl = useMemo(() => {
    let frontBumperP1ImageUrl: String =
      liftKitSku === 'L' || defaults?.liftKit?.sku === 'L'
        ? 'https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/f41bdf4c-32b5-43a8-6f7f-aca342fb5700' // Empty image
        : 'https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/9e70d1f3-ddd9-4816-534b-f2ddb1d82900'
    return frontBumperP1ImageUrl;
  }, [defaults, liftKitSku]);

  const frontBumperP2ImageUrl = useMemo(() => {
    let frontBumperP2ImageUrl: String =
      liftKitSku === 'L' || defaults?.liftKit?.sku === 'L'
        ? 'https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/f41bdf4c-32b5-43a8-6f7f-aca342fb5700' // Empty image
        : 'https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/52ac087f-c455-4897-5ded-039b23e43300'
    return frontBumperP2ImageUrl;
  }, [defaults, liftKitSku]);

  const [chassisImage, chassisImageStatus] = useImage(`${chassisImageUrl}/w=${width}`);
  const [wheelImage, wheelImageStatus] = useImage(`${wheelImageUrl}/w=${width}`);
  const [seatsImage, seatsImageStatus] = useImage(`${seatsImageUrl}/w=${width}`);
  const [bodyColorImage, bodyColorImageStatus] = useImage(`${bodyColorImageUrl}/w=${width}`);
  const [tireImage, tireImageStatus] = useImage(`${tireImageUrl}/w=${width}`);
  const [fenderFlaresImage, fenderFlaresImageStatus] = useImage(`${fenderFlaresImageUrl}/w=${width}`);
  const [brushGuardImage, brushguardImageStatus] = useImage(`${brushGuardImageUrl}/w=${width}`)
  const [frontBumperPartOne, frontBumperPartOneImageStatus] = useImage(`${frontBumperP1ImageUrl}/w=${width}`);
  const [frontBumperPartTwo, frontBumperPartTwoImageStatus] = useImage(`${frontBumperP2ImageUrl}/w=${width}`);
  const [rearStrutsImage, rearStrutsImageStatus] = useImage(`${rearStrutsImageUrl}/w=${width}`);
  const [sideMirrorsImage, sideMirrorsImageStatus] = useImage(`${sideMirrorsImageUrl}/w=${width}`);
  const [steeringWheelImage, steeringWheelImageStatus] = useImage(`${steeringWheelImageUrl}/w=${width}`);
  const [windshieldImage, windshieldImageStatus] = useImage(`${windshieldImageUrl}/w=${width}`);
  const [topImage, topImageStatus] = useImage(`${topImageUrl}/w=${width}`);
  const [decalsImage, decalsImageStatus] = useImage(`${decalsImageUrl}/w=${width}`);
  const [rearViewMirrorImage, rearViewMirrorImageStatus] = useImage(`${rearViewMirrorImageUrl}/w=${width}`);
  const [rockerPanelImage, rockerPanelImageStatus] = useImage(`${rockerPanelImageUrl}/w=${width}`);
  const [rearSeatCellPhoneHolder, rearSeatCellPhoneHolderStatus] = useImage(`${rearCellPhoneHolderImageUrl}/w=${width}`);
  const [dashImage, dashImageStatus] = useImage(`${dashImageUrl}/w=${width}`);

  // Center Image on canvas
  const x = useMemo<number>(() => {
    let x: number = 0;
    if (chassisImage !== undefined) {

      /* after the image is loaded, you can get it's dimensions */
      const imageWidth = chassisImage?.width;

      /* 
        calculate the offsets so the image is centered inside
        the canvas
      */
      const xOffset = (width - imageWidth) / 2;
      x = xOffset;
    }
    return x;
  }, [width, chassisImage]);

  const y = useMemo<number>(() => {
    let y: number = 0;
    if (chassisImage !== undefined) {

      /* after the image is loaded, you can get it's dimensions */
      const imageHeight = chassisImage?.height;

      /* 
        calculate the offsets so the image is centered inside
        the canvas
      */
      const yOffset = (height - imageHeight) / 2;
      y = yOffset;
    }
    return y;
  }, [height, chassisImage]);

  const loading =
    chassisImageStatus === 'loading' ||
    wheelImageStatus === 'loading' ||
    tireImageStatus === 'loading' ||
    bodyColorImageStatus === 'loading' ||
    fenderFlaresImageStatus === 'loading' ||
    brushguardImageStatus === 'loading' ||
    seatsImageStatus === 'loading' ||
    steeringWheelImageStatus === 'loading' ||
    windshieldImageStatus === 'loading' ||
    topImageStatus === 'loading' ||
    decalsImageStatus === 'loading' ||
    dashImageStatus === 'loading' ||
    rearViewMirrorImageStatus === 'loading' ||
    sideMirrorsImageStatus === 'loading' ||
    frontBumperPartOneImageStatus === 'loading' ||
    frontBumperPartTwoImageStatus === 'loading' ||
    rockerPanelImageStatus === 'loading' ||
    rearSeatCellPhoneHolderStatus === 'loading' ||
    rearStrutsImageStatus === 'loading';

  return (
    <CartModelDisplayLoading
      width={width}
      height={height}
      loading={loading}>
      <Stage width={width} height={height}>
        <Layer>
          <Image image={chassisImage} x={x} y={y} />
          <Image image={frontBumperPartTwo} x={x} y={y} />
          <Image image={decalsImage} x={x} y={y} />
          <Image image={tireImage} x={x} y={y} />
          <Image image={wheelImage} x={x} y={y} />
          <Image image={bodyColorImage} x={x} y={y} />
          <Image image={fenderFlaresImage} x={x} y={y} />
          <Image image={seatsImage} x={x} y={y} />
          <Image image={rearStrutsImage} x={x} y={y} />
          <Image image={sideMirrorsImage} x={x} y={y} />
          <Image image={steeringWheelImage} x={x} y={y} />
          <Image image={dashImage} x={x} y={y} />
          <Image image={windshieldImage} x={x} y={y} />
          <Image image={rearViewMirrorImage} x={x} y={y} />
          <Image image={topImage} x={x} y={y} />
          <Image image={frontBumperPartOne} x={x} y={y} />
          <Image image={brushGuardImage} x={x} y={y} />
          <Image image={rockerPanelImage} x={x} y={y} />
          <Image image={rearSeatCellPhoneHolder} x={x} y={y} />
        </Layer>
      </Stage>
    </CartModelDisplayLoading>
  )
};

export default X4Model;
